/**
 * MetadataEditorContext.tsx
 *
 * Local React state for the metadata editor modal.
 * No Redux dependency — all state is managed via React context.
 */

import React, { createContext, useContext, useMemo, useReducer } from 'react';
import type { PreparedFile, CreativeBLSettings } from '../contracts';
import type { Dictionary } from '../legacy_models/model';
import type { MetadataDraftFile, MetadataDraftValidationState } from '../metadataDomain';
import { validateMetadataDraftFiles } from '../metadataDomain';

// ─── State ───────────────────────────────────────────────────────────────────

export type MetadataEditorFile = MetadataDraftFile;

export interface MetadataEditorState {
  open: boolean;
  files: MetadataEditorFile[];
  excludedFiles: MetadataEditorFile[];
  dictionaries: Dictionary[];
  context: CreativeBLSettings | null;
  occupiedFilenames: string[];
  submitInProgress: boolean;
}

export type MetadataEditorValidationState = MetadataDraftValidationState;

const initialState: MetadataEditorState = {
  open: false,
  files: [],
  excludedFiles: [],
  dictionaries: [],
  context: null,
  occupiedFilenames: [],
  submitInProgress: false,
};

// ─── Actions ─────────────────────────────────────────────────────────────────

type Action =
  | {
      type: 'OPEN';
      files: MetadataEditorFile[];
      context: CreativeBLSettings;
      occupiedFilenames?: string[];
    }
  | { type: 'CLOSE' }
  | { type: 'REMOVE_FILE'; fileId: string }
  | { type: 'REMOVE_RELPATH_PREFIX'; relpath: string }
  | { type: 'INCLUDE_FILE'; fileId: string }
  | { type: 'SET_META'; fileId: string; name: string; value: string }
  | { type: 'MASS_FILL'; dictName: string; value: string }
  | { type: 'SET_SUBMIT_IN_PROGRESS'; value: boolean };

function matchesRelpathPrefix(file: MetadataEditorFile, relpath: string) {
  if (!file.relpath) return false;
  return file.relpath === relpath || file.relpath.startsWith(`${relpath}/`);
}

function reducer(state: MetadataEditorState, action: Action): MetadataEditorState {
  switch (action.type) {
    case 'OPEN': {
      const includedFiles = action.files.filter((file) => !file.excluded);
      const excludedFiles = action.files.filter((file) => file.excluded);

      return {
        ...state,
        open: true,
        files: includedFiles,
        excludedFiles,
        context: action.context,
        dictionaries: action.context.dictionaries,
        occupiedFilenames: action.occupiedFilenames ?? [],
        submitInProgress: false,
      };
    }
    case 'CLOSE':
      return initialState;
    case 'REMOVE_FILE': {
      const removedFile = state.files.find((file) => file.id === action.fileId);

      return {
        ...state,
        files: state.files.filter((f) => f.id !== action.fileId),
        excludedFiles: removedFile
          ? [
              ...state.excludedFiles,
              { ...removedFile, excluded: true, exclusionReason: 'Moved to Excluded by user.' },
            ]
          : state.excludedFiles,
      };
    }
    case 'REMOVE_RELPATH_PREFIX': {
      const removedBranch = state.files.filter((file) =>
        matchesRelpathPrefix(file, action.relpath),
      );

      return {
        ...state,
        files: state.files.filter((f) => {
          if (!f.relpath) return true;
          return !(f.relpath === action.relpath || f.relpath.startsWith(`${action.relpath}/`));
        }),
        excludedFiles: [
          ...state.excludedFiles,
          ...removedBranch.map((file) => ({
            ...file,
            excluded: true,
            exclusionReason: `Moved to Excluded from folder ${action.relpath}.`,
          })),
        ],
      };
    }
    case 'INCLUDE_FILE': {
      const restoredFile = state.excludedFiles.find((file) => file.id === action.fileId);

      return {
        ...state,
        files: restoredFile
          ? [...state.files, { ...restoredFile, excluded: false, exclusionReason: undefined }]
          : state.files,
        excludedFiles: state.excludedFiles.filter((file) => file.id !== action.fileId),
      };
    }
    case 'SET_META':
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.fileId ? { ...f, meta: { ...f.meta, [action.name]: action.value } } : f,
        ),
      };
    case 'MASS_FILL':
      return {
        ...state,
        files: state.files.map((f) => ({
          ...f,
          meta: { ...f.meta, [action.dictName]: action.value },
        })),
      };
    case 'SET_SUBMIT_IN_PROGRESS':
      return { ...state, submitInProgress: action.value };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface MetadataEditorContextValue {
  state: MetadataEditorState;
  dispatch: React.Dispatch<Action>;
  /** Computed new filenames: Record<fileId, newFilename> */
  newFilenames: Record<string, string>;
  validation: MetadataEditorValidationState;
}

const MetadataEditorCtx = createContext<MetadataEditorContextValue | null>(null);

export function useMetadataEditor() {
  const ctx = useContext(MetadataEditorCtx);
  if (!ctx) throw new Error('useMetadataEditor must be used inside MetadataEditorProvider');
  return ctx;
}

// ─── Resolve/Reject callbacks (set by the service) ──────────────────────────

type ResolveReject = {
  resolve: (files: PreparedFile[] | null) => void;
};

const callbacksRef: { current: ResolveReject | null } = { current: null };

export function _setCallbacks(cbs: ResolveReject | null) {
  callbacksRef.current = cbs;
}

export function _getCallbacks() {
  return callbacksRef.current;
}

// ─── Provider ────────────────────────────────────────────────────────────────

export const MetadataEditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { newFilenames, validation } = useMemo(
    () =>
      validateMetadataDraftFiles({
        files: state.files,
        excludedFilesCount: state.excludedFiles.length,
        dictionaries: state.dictionaries,
        occupiedFilenames: state.occupiedFilenames,
        submitInProgress: state.submitInProgress,
      }),
    [
      state.files,
      state.excludedFiles.length,
      state.dictionaries,
      state.occupiedFilenames,
      state.submitInProgress,
    ],
  );

  const value = useMemo(
    () => ({ state, dispatch, newFilenames, validation }),
    [state, dispatch, newFilenames, validation],
  );

  return <MetadataEditorCtx.Provider value={value}>{children}</MetadataEditorCtx.Provider>;
};
